import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToolsService } from 'src/app/api/tools.service';
import { CommonModule } from '@angular/common';
import { CardModule } from 'primeng/card';
import { CustomerService } from 'src/app/api';
import { TripService } from 'src/app/api';
import { Customer } from 'src/app/api';
import { UiModule } from 'src/app/ui/ui.module';
import { MessageService } from 'primeng/api';
import { Router, ActivatedRoute } from "@angular/router";
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-delete-record',
  imports: [CommonModule, ReactiveFormsModule, CardModule, UiModule],
  templateUrl: './delete-record.component.html',
  styleUrl: './delete-record.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeleteRecordComponent {

  form = new FormGroup({
    sourceCustomerID: new FormControl('', Validators.required)
  });
  confirmState = signal(false);
  isLoading = signal(false);
  isApproving = signal(false);
  hasSearched = signal(false);
  customerId = signal<number>(0);
  customer = signal<Customer | null>(null);
  trips = signal<any[]>([]);
  tripId = signal<number>(0);
  errorMessage = signal<string | null>(null);

  constructor(
    private toolsService: ToolsService,
    private customerService: CustomerService,
    private tripService: TripService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  onCheckCustomer() {
    if (this.form.valid) {

      this.confirmState.set(false);
      this.isLoading.set(true);
      this.errorMessage.set(null);
      const id = Number(this.form.value.sourceCustomerID!);
      this.customerId.set(id);
      this.customerService.getCustomer(id).subscribe({
        next: (customer) => {
          this.customer.set(customer);
          this.tripService.getTrip(id).subscribe({
            next: (trips) => {
              this.trips.set([trips].filter((t: any) => !t.dropped));
              this.confirmState.set(true);
              this.isLoading.set(false);
              this.hasSearched.set(true);
            },
            error: () => {
              this.isLoading.set(false);
              this.trips.set([]);
              this.confirmState.set(true);
              this.hasSearched.set(true);
            }
          });
        },
        error: () => {
          this.isLoading.set(false);
          this.customer.set(null);
          this.trips.set([]);
          this.confirmState.set(false);
          this.errorMessage.set('Customer not found');
          this.hasSearched.set(true);
        }
      });
    }
  }

  onDeleteCustomer() {
    this.isApproving.set(true);
    const id = this.customerId();
    if (!id) return;

    this.toolsService.deleteCustomer(id).subscribe({
      next: (result) => {
        this.isApproving.set(false);

        if (result.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: result.message || 'Customer successfully deleted'
          });
          setTimeout(() => {
            this.router.navigate(['/mission-control']);
          }, 1500);
        } else if (result.hasTrips) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Warning',
            detail: result.message || 'Please unassign any trips from this account before deleting it'
          });

          this.trips.set([{ id }]);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isApproving.set(false);
        let errorMessage = 'Failed to delete customer';
        if (error.error?.error?.message) {
          errorMessage = error.error.error.message;
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        } else if (typeof error.error === 'string') {
          errorMessage = error.error;
        } else if (error.status === 400 && error.statusText) {
          errorMessage = `Bad Request: ${error.statusText}`;
        }
        this.errorMessage.set(errorMessage);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage
        });

        if (errorMessage.includes('unassign')) {
          this.trips.set([{ id }]);
        }
      }
    });
  }

  ngOnInit() {
    const custIdParam = this.route.snapshot.paramMap.get('custId');
    const custIdQuery = this.route.snapshot.queryParamMap.get('custId');

    const custId = custIdParam || custIdQuery;

    if (custId) {
      this.form.patchValue({ sourceCustomerID: custId });
      this.onCheckCustomer();
    }
  }

  onCancel() {
    this.confirmState.set(false);
    this.router.navigate(['/mission-control']);
  }

  navigateToCustomerDetails() {
    this.router.navigate(['contact-card', this.customerId()]);
  }
}